import React, { useState, useEffect, useRef } from 'react';
import { Card, CardHeader, CardBody, Alert, Row, Col, Badge, Nav, NavItem, NavLink, TabContent, TabPane, Table } from 'reactstrap';
import Select from 'react-select';
import classnames from 'classnames';
import { useTranslation } from 'react-multi-lang';
import { DateTime } from 'luxon';
import QuarriesService from '../Services/QuarriesService';
import CheckInsService from '../Services/CheckInsService';
import FaultsService from '../Services/FaultsService';
import { MachinesService } from '../Services/MachinesService';
import { SystemService } from '../Services/SystemService';
import AuthService from '../Services/AuthService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTruck, faList, faMap, faUser, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import ReactHTMLTableToExcel from 'react-html-table-to-excel';

const MachineMap = () => {
    const t = useTranslation();
    const [activeQuarry, setActiveQuarry] = useState(null);
    const [currentConfig, setCurrentConfig] = useState(null);
    const [activeCheckIns, setActiveCheckIns] = useState([]);
    const [enrichedCheckIns, setEnrichedCheckIns] = useState([]);
    const [allMachines, setAllMachines] = useState([]);
    const [activeFaults, setActiveFaults] = useState([]);
    const [activeTab, setActiveTab] = useState('map');
    const [shelves, setShelves] = useState([]);
    const [now, setNow] = useState(DateTime.now());

    // Filters
    const [filterCategory, setFilterCategory] = useState(null);
    const [filterSubcategory, setFilterSubcategory] = useState(null);
    const [filterMachine, setFilterMachine] = useState(null);
    const [filterShelf, setFilterShelf] = useState(null);
    const canvasRef = useRef(null);
    const [image, setImage] = useState(null);
    const [zoom, setZoom] = useState(1);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

    const quarriesService = new QuarriesService();
    const checkInsService = new CheckInsService();
    const faultsService = new FaultsService();
    const machinesService = new MachinesService();
    const systemService = new SystemService();
    const authService = new AuthService();
    const baseUrl = systemService.getBaseUrl().replace(/\/$/, '');

    useEffect(() => {
        const config = authService.getConfig();
        if (config) {
            setCurrentConfig(config);
            fetchData(config.whCode);
        }
        
        const interval = setInterval(() => {
            setNow(DateTime.now());
            const freshConfig = authService.getConfig();
            if (freshConfig && freshConfig.whCode) {
                fetchActiveCheckIns(freshConfig.whCode);
                fetchActiveFaults(freshConfig.whCode);
            }
        }, 10000);

        return () => clearInterval(interval);
    }, []);

    const fetchData = async (whCode) => {
        try {
            const quarryRes = await quarriesService.getQuarryByWhCode(whCode);
            setActiveQuarry(quarryRes.data);
            if (quarryRes.data && quarryRes.data.topologyMap) {
                const img = new Image();
                img.src = `${baseUrl}/uploads/topology-maps/${quarryRes.data.topologyMap}`;
                img.onload = () => {
                    setImage(img);
                };
            }
            
            // Get shelves from config if no map or for grid view
            const config = authService.getAvailableConfigs().find(c => c.whCode === whCode);
            if (config && config.shelves) {
                setShelves(config.shelves);
            }

            await fetchActiveCheckIns(whCode);
            await fetchActiveFaults(whCode);
            await fetchMachines(whCode);
        } catch (e) {
            console.error(e);
        }
    };

    const fetchActiveCheckIns = async (whCode) => {
        try {
            const data = await checkInsService.getActiveCheckIns(whCode);
            setActiveCheckIns(data);
        } catch (e) {
            console.error(e);
        }
    };

    const fetchActiveFaults = async (whCode) => {
        try {
            const data = await faultsService.getFaults({ wh: whCode, isResolved: false });
            setActiveFaults(data);
        } catch (e) {
            console.error(e);
        }
    };

    const fetchMachines = async (whCode) => {
        try {
            const data = await machinesService.getMachines(whCode);
            setAllMachines(data);
        } catch (e) {
            console.error(e);
        }
    };

    const getMachineStatusColor = (checkIn) => {
        const hasFault = activeFaults.some(f => f.machineId === checkIn.machineId);
        if (hasFault) return '#dc3545'; // Red for Damage
        if (checkIn.type === 'WAITING') return '#ffc107'; // Yellow for Waiting
        return '#28a745'; // Green for Active
    };

    const formatDuration = (startTimeISO) => {
        if (!startTimeISO) return '';
        const start = DateTime.fromISO(startTimeISO);
        const diff = now.diff(start, ['hours', 'minutes', 'seconds']);
        
        const hours = Math.floor(diff.hours);
        const minutes = Math.floor(diff.minutes);
        const seconds = Math.floor(diff.seconds);
        
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };

    const filteredEnrichedCheckIns = enrichedCheckIns.filter(ci => {
        const machine = allMachines.find(m => m.Code === ci.machineId);
        if (filterCategory && machine?.Code1 !== filterCategory.value) return false;
        if (filterSubcategory && machine?.Code2 !== filterSubcategory.value) return false;
        if (filterMachine && ci.machineId !== filterMachine.value) return false;
        if (filterShelf && ci.area !== filterShelf.value) return false;
        return true;
    });

    useEffect(() => {
        if (activeCheckIns.length > 0 && allMachines.length > 0) {
            const enriched = activeCheckIns.map(ci => {
                const machine = allMachines.find(m => m.Code === ci.machineId);
                return {
                    ...ci,
                    machine: machine ? {
                        ...ci.machine,
                        name: machine.Description || ci.machine?.name,
                        description: machine.Description || ci.machine?.description
                    } : ci.machine
                };
            });
            setEnrichedCheckIns(enriched);
        } else {
            setEnrichedCheckIns(activeCheckIns);
        }
    }, [activeCheckIns, allMachines]);

    useEffect(() => {
        if (image) {
            draw();
        }
    }, [image, filteredEnrichedCheckIns, activeFaults, zoom, offset]);

    const formatFaultTime = (createdAt) => {
        if (!createdAt) return '';
        const faultDate = DateTime.fromISO(createdAt).setZone('Europe/Athens');
        const nowInAthens = DateTime.now().setZone('Europe/Athens');
        
        if (faultDate.hasSame(nowInAthens, 'day')) {
            return faultDate.toFormat('HH:mm');
        } else {
            return faultDate.toFormat('dd/MM HH:mm');
        }
    };

    const draw = () => {
        const canvas = canvasRef.current;
        if (!canvas || !image) return;
        const ctx = canvas.getContext('2d');
        
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.save();
        ctx.translate(offset.x, offset.y);
        ctx.scale(zoom, zoom);
        
        // Draw Map
        ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
        
        // Draw Polygons
        if (activeQuarry && activeQuarry.shelves) {
            activeQuarry.shelves.forEach(shelf => {
                if (shelf.polygon && shelf.polygon.length > 0) {
                    ctx.beginPath();
                    ctx.moveTo(shelf.polygon[0].x * canvas.width, shelf.polygon[0].y * canvas.height);
                    for (let i = 1; i < shelf.polygon.length; i++) {
                        ctx.lineTo(shelf.polygon[i].x * canvas.width, shelf.polygon[i].y * canvas.height);
                    }
                    ctx.closePath();
                    ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
                    ctx.lineWidth = 1;
                    ctx.stroke();
                    ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
                    ctx.fill();
                }
            });
        }

        // Draw Machines
        filteredEnrichedCheckIns.forEach(checkIn => {
            const shelf = activeQuarry?.shelves?.find(s => s.name === checkIn.area);
            let x, y;

            if (shelf && shelf.polygon && shelf.polygon.length > 0) {
                // Find centroid of polygon
                x = shelf.polygon.reduce((sum, p) => sum + p.x, 0) / shelf.polygon.length;
                y = shelf.polygon.reduce((sum, p) => sum + p.y, 0) / shelf.polygon.length;
            } else {
                // If no polygon or shelf not found, we could draw in a dedicated area or just skip
                // For now, let's skip if not on map
                return;
            }

            const canvasX = x * canvas.width;
            const canvasY = y * canvas.height;

            // Pin color
            const color = getMachineStatusColor(checkIn);
            
            // Draw Vehicle Icon (simplified truck shape)
            ctx.fillStyle = color;
            ctx.beginPath();
            // Main body
            ctx.fillRect(canvasX - 10/zoom, canvasY - 5/zoom, 20/zoom, 10/zoom);
            // Cabin
            ctx.fillRect(canvasX + 6/zoom, canvasY - 10/zoom, 6/zoom, 6/zoom);
            // Wheels
            ctx.beginPath();
            ctx.arc(canvasX - 6/zoom, canvasY + 6/zoom, 3/zoom, 0, 2 * Math.PI);
            ctx.arc(canvasX + 6/zoom, canvasY + 6/zoom, 3/zoom, 0, 2 * Math.PI);
            ctx.fillStyle = '#333';
            ctx.fill();

            // Label
            const machineName = checkIn.machine?.name || '';
            const userName = checkIn.user?.name || '';
            const text = `${checkIn.machineId}${machineName ? ` - ${machineName}` : ''}${userName ? ` (${userName})` : ''}`;
            ctx.font = `bold ${10/zoom}px Arial`;
            const metrics = ctx.measureText(text);
            ctx.fillStyle = 'rgba(0,0,0,0.7)';
            ctx.fillRect(canvasX - metrics.width/2 - 2/zoom, canvasY - 25/zoom, metrics.width + 4/zoom, 12/zoom);
            ctx.fillStyle = 'white';
            ctx.textAlign = 'center';
            ctx.fillText(text, canvasX, canvasY - 16/zoom);
        });

        ctx.restore();
    };

    const handleWheel = (e) => {
        e.preventDefault();
        const scaleFactor = 1.1;
        const delta = e.deltaY > 0 ? 1 / scaleFactor : scaleFactor;
        const newZoom = zoom * delta;
        if (newZoom >= 1 && newZoom <= 10) {
            // Zoom towards mouse position
            const rect = canvasRef.current.getBoundingClientRect();
            const mouseX = e.clientX - rect.left;
            const mouseY = e.clientY - rect.top;
            
            const newOffsetX = mouseX - (mouseX - offset.x) * delta;
            const newOffsetY = mouseY - (mouseY - offset.y) * delta;
            
            setZoom(newZoom);
            setOffset({ x: newOffsetX, y: newOffsetY });
        }
    };

    const handleMouseDown = (e) => {
        setIsDragging(true);
        setDragStart({ x: e.clientX - offset.x, y: e.clientY - offset.y });
    };

    const handleMouseMove = (e) => {
        if (isDragging) {
            setOffset({
                x: e.clientX - dragStart.x,
                y: e.clientY - dragStart.y
            });
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const toggleTab = tab => {
        if (activeTab !== tab) setActiveTab(tab);
    };

    if (!currentConfig) return <div className="p-4"><Alert color="info">{t('quarries.selectQuarry') || 'Please select a quarry from the menu above.'}</Alert></div>;

    const machinesOffMap = filteredEnrichedCheckIns.filter(ci => !activeQuarry?.shelves?.find(s => s.name === ci.area)?.polygon);

    // Filter shelves to only those that have active check-ins
    const shelvesWithCheckIns = shelves.filter(shelf => filteredEnrichedCheckIns.some(ci => ci.area === shelf));

    // Derive options for filters
    const categoryOptions = [...new Set(allMachines.map(m => m.Code1).filter(Boolean))].map(c => ({ value: c, label: c }));
    const subcategoryOptions = [...new Set(allMachines
        .filter(m => !filterCategory || m.Code1 === filterCategory.value)
        .map(m => m.Code2).filter(Boolean))].map(c => ({ value: c, label: c }));
    const machineOptions = allMachines
        .filter(m => !filterCategory || m.Code1 === filterCategory.value)
        .filter(m => !filterSubcategory || m.Code2 === filterSubcategory.value)
        .map(m => ({ value: m.Code, label: `${m.Code} - ${m.Description}` }));
    const shelfOptions = shelves.map(s => ({ value: s, label: s }));

    return (
        <div style={{ margin: '-20px -20px 0 -20px' }}>
            <Card className="border-0 shadow-none">
                <CardHeader className="bg-primary text-white p-2">
                    <Row className="align-items-center">
                        <Col>
                            <h4 className="m-0 ms-2">{t('sideBar.machineMap') || 'Machine Locations'}: {currentConfig.name}</h4>
                        </Col>
                        <Col xs="auto">
                            <Nav tabs className="border-0">
                                <NavItem>
                                    <NavLink
                                        className={classnames({ active: activeTab === 'map', 'text-white': activeTab !== 'map', 'bg-light text-dark': activeTab === 'map' })}
                                        onClick={() => { toggleTab('map'); }}
                                        style={{ cursor: 'pointer', border: 'none', borderRadius: '5px 5px 0 0' }}
                                    >
                                        <FontAwesomeIcon icon={faMap} className="me-2" />
                                        {t('machineMap.map') || 'Map'}
                                    </NavLink>
                                </NavItem>
                                <NavItem>
                                    <NavLink
                                        className={classnames({ active: activeTab === 'list', 'text-white': activeTab !== 'list', 'bg-light text-dark': activeTab === 'list' })}
                                        onClick={() => { toggleTab('list'); }}
                                        style={{ cursor: 'pointer', border: 'none', borderRadius: '5px 5px 0 0' }}
                                    >
                                        <FontAwesomeIcon icon={faList} className="me-2" />
                                        {t('machineMap.list') || 'List'}
                                    </NavLink>
                                </NavItem>
                            </Nav>
                        </Col>
                    </Row>
                </CardHeader>
                <CardBody className="p-0">
                    <div className="bg-light p-2 border-bottom">
                        <Row className="g-2">
                            <Col md={3}>
                                <Select
                                    isClearable
                                    placeholder={t('volumes.category')}
                                    options={categoryOptions}
                                    value={filterCategory}
                                    onChange={(val) => { setFilterCategory(val); setFilterSubcategory(null); }}
                                    styles={{ menu: base => ({ ...base, zIndex: 9999 }) }}
                                />
                            </Col>
                            <Col md={3}>
                                <Select
                                    isClearable
                                    placeholder={t('volumes.subcategory')}
                                    options={subcategoryOptions}
                                    value={filterSubcategory}
                                    onChange={setFilterSubcategory}
                                    styles={{ menu: base => ({ ...base, zIndex: 9999 }) }}
                                />
                            </Col>
                            <Col md={3}>
                                <Select
                                    isClearable
                                    placeholder={t('checkIns.machine')}
                                    options={machineOptions}
                                    value={filterMachine}
                                    onChange={setFilterMachine}
                                    styles={{ menu: base => ({ ...base, zIndex: 9999 }) }}
                                />
                            </Col>
                            <Col md={3}>
                                <Select
                                    isClearable
                                    placeholder={t('checkIns.area')}
                                    options={shelfOptions}
                                    value={filterShelf}
                                    onChange={setFilterShelf}
                                    styles={{ menu: base => ({ ...base, zIndex: 9999 }) }}
                                />
                            </Col>
                        </Row>
                    </div>
                    <TabContent activeTab={activeTab}>
                        <TabPane tabId="map">
                            <div 
                                onWheel={handleWheel}
                                onMouseDown={handleMouseDown}
                                onMouseMove={handleMouseMove}
                                onMouseUp={handleMouseUp}
                                onMouseLeave={handleMouseUp}
                                style={{ position: 'relative', overflow: 'hidden', borderBottom: '1px solid #ccc', cursor: image ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
                            >
                                {!image && (
                                    <div className="p-3">
                                        <Alert color="warning" className="text-center">
                                            {t('quarries.noMap') || 'No topology map uploaded for this quarry.'}
                                        </Alert>
                                        <div className="shelf-grid">
                                            <Row>
                                                {shelves.filter(s => !filterShelf || s === filterShelf.value).map(shelf => {
                                                    const machinesInShelf = filteredEnrichedCheckIns.filter(ci => ci.area === shelf);
                                                    if (filterShelf && machinesInShelf.length === 0 && shelf !== filterShelf.value) return null;
                                                    if ((filterCategory || filterSubcategory || filterMachine) && machinesInShelf.length === 0) return null;
                                                    return (
                                                        <Col key={shelf} xs={6} md={4} lg={3} className="p-1">
                                                            <div 
                                                                className="shelf-grid-cell" 
                                                                title={machinesInShelf.map(m => `${m.machineId}${m.machine?.name ? ` - ${m.machine.name}` : ''}${m.user?.name ? ` (${m.user.name})` : ''}`).join('\n')}
                                                            >
                                                                <div className="shelf-name">{shelf}</div>
                                                                <div className="machines-container">
                                                                    {machinesInShelf.map(m => (
                                                                        <div key={m.id} className="machine-item">
                                                                            <div className="machine-label text-truncate" title={`${m.machineId}${m.machine?.name ? ` - ${m.machine.name}` : ''}${m.user?.name ? ` (${m.user.name})` : ''}`}>
                                                                                {m.machineId}{m.machine?.name ? ` - ${m.machine.name}` : ''}
                                                                            </div>
                                                                            <div className="machine-icon-container" style={{ color: getMachineStatusColor(m) }}>
                                                                                <FontAwesomeIcon icon={faTruck} />
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                                {machinesInShelf.length > 1 && (
                                                                    <div className="machine-count-badge">
                                                                        {machinesInShelf.length}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </Col>
                                                    );
                                                })}
                                            </Row>
                                        </div>
                                    </div>
                                )}
                                {image && (
                                    <canvas 
                                        ref={canvasRef} 
                                        width={1200} 
                                        height={600} 
                                        style={{ display: 'block'}}
                                    />
                                )}
                                <div style={{ position: 'absolute', bottom: 10, right: 10, background: 'rgba(255,255,255,0.8)', padding: 5, borderRadius: 5 }}>
                                    <div className="small">
                                        <span className="me-2"><Badge color="success" style={{ backgroundColor: '#28a745' }}> </Badge> {t('checkIns.typeWork')}</span>
                                        <span className="me-2"><Badge color="warning" style={{ backgroundColor: '#ffc107' }}> </Badge> {t('checkIns.typeWaiting')}</span>
                                        <span><Badge color="danger" style={{ backgroundColor: '#dc3545' }}> </Badge> {t('checkIns.damage')}</span>
                                    </div>
                                    <div className="x-small text-muted mt-1">{t('machineMap.zoomPanInfo') || 'Scroll to zoom, drag to pan'}</div>
                                </div>
                            </div>
                        </TabPane>
                        <TabPane tabId="list">
                            <div className="p-4">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h5 className="mb-0">{t('machineMap.shelvesAndVehicles') || 'Shelves and Vehicles'}</h5>
                                    <ReactHTMLTableToExcel
                                        id="machineMapExcelBtn"
                                        className="btn btn-sm btn-success fw-bold"
                                        table="machineMapExportTable"
                                        filename={`machines_${currentConfig?.name || 'quarry'}_${DateTime.now().setZone('Europe/Athens').toFormat('yyyyMMdd_HHmm')}`}
                                        sheet="Machines"
                                        buttonText={t('machineMap.exportExcel') || "Excel"}
                                    />
                                </div>
                                {shelvesWithCheckIns.length === 0 ? (
                                    <Alert color="info">{t('machineMap.noActiveVehicles') || 'No active vehicles found in any shelf.'}</Alert>
                                ) : (
                                    <Table responsive hover striped>
                                        <thead>
                                            <tr>
                                                <th>{t('checkIns.area') || 'Shelf'}</th>
                                                <th>{t('machineMap.vehicles') || 'Vehicles'}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {shelvesWithCheckIns.map(shelf => {
                                                const machines = filteredEnrichedCheckIns.filter(ci => ci.area === shelf);
                                                return (
                                                    <tr key={shelf}>
                                                        <td className="align-middle"><strong>{shelf}</strong></td>
                                                        <td>
                                                            <div className="d-flex flex-wrap">
                                                                {machines.map(m => {
                                                                    const statusColor = getMachineStatusColor(m);
                                                                    const machineFaults = activeFaults.filter(f => f.machineId === m.machineId);
                                                                    const hasFault = machineFaults.length > 0;
                                                                    const activeMachine = allMachines.find(am => am.Code === m.machineId);
                                                                    return (
                                                                        <Card key={m.id} body className="p-2 me-2 mb-2" style={{ minWidth: '200px', borderLeft: `5px solid ${statusColor}` }}>
                                                                            <div className="d-flex justify-content-between align-items-center">
                                                                                <span className="fw-bold"><FontAwesomeIcon icon={faTruck} className="me-1" />{m.machineId}</span>
                                                                                <Badge style={{ backgroundColor: statusColor }}>
                                                                                    {hasFault ? (t('checkIns.damage') || 'Damage') : 
                                                                                     m.type === 'WAITING' ? (t('checkIns.typeWaiting') || 'Waiting') : (t('checkIns.typeWork') || 'Work')}
                                                                                </Badge>
                                                                            </div>
                                                                            <div className="small text-muted text-truncate">{m.machine?.name}</div>
                                                                            <div className="small mt-1"><FontAwesomeIcon icon={faUser} className="me-1" /> {m.user?.name}</div>
                                                                            {hasFault && (
                                                                                <div className="small mt-1 text-danger">
                                                                                    <strong>{t('faults.declared') || 'Declared'}:</strong> {formatFaultTime(machineFaults[0].createdAt)}
                                                                                </div>
                                                                            )}
                                                                            {!hasFault && m.type === 'WAITING' && (
                                                                                <div className="small mt-1 text-warning">
                                                                                    <strong>{t('checkIns.waitingFor') || 'Waiting'}:</strong> {formatDuration(m.timeFrom)}
                                                                                </div>
                                                                            )}
                                                                        </Card>
                                                                    );
                                                                })}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                )}

                                {/* Hidden Table for Clean Excel Export */}
                                <Table id="machineMapExportTable" className="d-none">
                                    <thead>
                                        <tr>
                                            <th>{t('checkIns.area') || 'Πατάρι'}</th>
                                            <th>{t('machineMap.machine') || 'Μηχάνημα'}</th>
                                            <th>{t('machines.description') || 'Περιγραφή'}</th>
                                            <th>{t('machineMap.category') || 'Κατηγορία'}</th>
                                            <th>{t('machineMap.subcategory') || 'Υποκατηγορία'}</th>
                                            <th>{t('machineMap.driver') || 'Χειριστής'}</th>
                                            <th>{t('machineMap.status') || 'Κατάσταση'}</th>
                                            <th>{t('machineMap.faultReason') || 'Αιτία Βλάβης'}</th>
                                            <th>{t('machineMap.duration') || 'Διάρκεια'}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredEnrichedCheckIns.map(ci => {
                                            const ciFaults = activeFaults.filter(f => f.machineId === ci.machineId);
                                            const hasFault = ciFaults.length > 0;
                                            const statusText = hasFault ? (t('checkIns.damage') || 'Βλάβη') : 
                                                ci.type === 'WAITING' ? (t('checkIns.typeWaiting') || 'Αναμονή') : (t('checkIns.typeWork') || 'Εργασία');
                                            const faultReason = hasFault ? ciFaults[0].reason || '-' : '-';
                                            const duration = formatDuration(ci.timeFrom);
                                            return (
                                                <tr key={`export-${ci.id}`}>
                                                    <td>{ci.area || '-'}</td>
                                                    <td>{ci.machineId || ''}</td>
                                                    <td>{ci.machine?.name || ''}</td>
                                                    <td>{ci.machine?.category || ci.machine?.Code1 || ''}</td>
                                                    <td>{ci.machine?.subcategory || ci.machine?.Code2 || ''}</td>
                                                    <td>{ci.user?.name || '-'}</td>
                                                    <td>{statusText}</td>
                                                    <td>{faultReason}</td>
                                                    <td>{duration}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        </TabPane>
                    </TabContent>

                    <div className="p-4 pt-0">
                        {enrichedCheckIns.length > 0 && activeTab === 'map' && (
                            <div className="mt-3">
                                <h5>{t('machineMap.activeMachines') || 'Active Machines'}:</h5>
                                <Row>
                                    {enrichedCheckIns.map(ci => {
                                    const ciFaults = activeFaults.filter(f => f.machineId === ci.machineId);
                                    const hasFault = ciFaults.length > 0;
                                    return (
                                        <Col key={ci.id} md={3} className="mb-2">
                                            <Card body className="p-2">
                                                <div className="d-flex justify-content-between">
                                                    <span><strong>{ci.machineId}{ci.machine?.name ? ` - ${ci.machine.name}` : ''}</strong></span>
                                                    <Badge style={{ backgroundColor: getMachineStatusColor(ci) }}>
                                                        {hasFault ? (t('checkIns.damage') || 'Damage') :
                                                         ci.type === 'WAITING' ? (t('checkIns.typeWaiting') || 'Waiting') : (t('checkIns.typeWork') || 'Work')}
                                                    </Badge>
                                                </div>
                                                <div className="small"><strong>{t('checkIns.area') || 'Area'}:</strong> {ci.area}</div>
                                                <div className="small text-muted">{ci.machine?.name}</div>
                                                <div className="small text-muted">{ci.user?.name}</div>
                                                {hasFault && (
                                                    <div className="small mt-1 text-danger">
                                                        <strong>{t('faults.declared') || 'Declared'}:</strong> {formatFaultTime(ciFaults[0].createdAt)}
                                                    </div>
                                                )}
                                                {!hasFault && ci.type === 'WAITING' && (
                                                    <div className="small mt-1 text-warning">
                                                        <strong>{t('checkIns.waitingFor') || 'Waiting'}:</strong> {formatDuration(ci.timeFrom)}
                                                    </div>
                                                )}
                                            </Card>
                                        </Col>
                                    );
                                })}
                                </Row>
                            </div>
                        )}

                        {machinesOffMap.length > 0 && activeTab === 'map' && (
                            <div className="mt-3">
                                <hr />
                                <h5 className="text-muted small">{t('machineMap.notOnMap') || 'Machines not on map (No defined polygon for area)'}:</h5>
                                <div className="d-flex flex-wrap">
                                    {machinesOffMap.map(ci => (
                                        <Badge key={ci.id} color="secondary" className="me-2 mb-2" title={`${ci.machine?.name || ''}${ci.user?.name ? ` (${ci.user.name})` : ''}`}>
                                            {ci.machineId} ({ci.area}){ci.machine?.name ? ` - ${ci.machine.name}` : ''}{ci.user?.name ? ` (${ci.user.name})` : ''}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </CardBody>
            </Card>
        </div>
    );
};

export default MachineMap;
